import React, { Component } from 'react';
import {
  bool, func, number
} from 'prop-types';
import { connect } from 'react-redux';
import { range, last } from 'lodash-es';
import { Translate } from 'react-localize-redux';
import { variableEditorType, variableTypeValues } from '../../../../constants/types';
import { updateVariableAction } from '../../../../actions';
import { getTranslation, withLang, langType } from '../../../../i18n';
import { assignArrayItemInObject } from '../../../../util';
import { AVAILABLE_LANGUAGES } from '../../../../constants';

import TabsContainer from '../../../TabsContainer';
import TabPane from '../../../TabPane';
import { LocalizedRichTextArea } from '../../../LocalizedInput';
import AbsoluteVariableSummary from '../../../VariableSummary/AbsoluteVariableSummary';
import ResponseCategoryDescription from './ResponseCategoryDescription';

import sharedStyles from '../../../../styles/shared';
import editorStyles from '../../editor.module.css';
import styles from './absoluteVariableFeedback.module.css';

const TRANSLATION_NAMESPACE = 'surveySchemaParts.dimensions.absoluteFeedback';

const getMinVariableValue = (variable) => variable.questions.length;

const getMaxVariableValue = (variable) => {
  const { questions } = variable;
  return questions.length ? questions[0].responseOptions.length * questions.length : 0;
};

const getMinResponseCategoryValue = (variable, categoryIndex) => {
  if (categoryIndex === 0) {
    return getMinVariableValue(variable);
  }
  return variable.questions.length
    ? variable.responseCategories[categoryIndex - 1].absoluteCategory.value + 1 : 0;
};

const getMaxResponseCategoryValue = (variable, categoryIndex) => {
  if (categoryIndex + 1 === variable.responseCategories.length) {
    return getMaxVariableValue(variable);
  }
  return variable.questions.length
    ? variable.responseCategories[categoryIndex + 1].absoluteCategory.value - 1 : 0;
};

const variableToStats = (variable, category) => ({
  type: variableTypeValues.ABSOLUTE,
  responseOptionValues: range(1, variable.questions.length
    ? variable.questions[0].responseOptions.length + 1 : 1),
  absoluteCategories: variable.responseCategories.map((rc) => rc.absoluteCategory),
  name: {},
  average: 0,
  averageTotal: category.absoluteCategory.value,
  minTotal: getMinVariableValue(variable),
  maxTotal: getMaxVariableValue(variable),
  distribution: {},
  order: 1
});

class AbsoluteVariableFeedback extends Component {
  constructor(props) {
    super(props);

    this.onAddCategory = this.onAddCategory.bind(this);
    this.onRemoveCategory = this.onRemoveCategory.bind(this);
    this.onLocalizedRTEChange = this.onLocalizedRTEChange.bind(this);
    this.onCategoryValueChange = this.onCategoryValueChange.bind(this);
    this.onCategoryDescriptionChange = this.onCategoryDescriptionChange.bind(this);
    this.renderCategoryTab = this.renderCategoryTab.bind(this);
  }

  onCategoryDescriptionChange(index, lang, value) {
    this.updateResponseCategories(index, {
      absoluteCategory: { description: { [lang]: value } }
    });
  }

  onCategoryValueChange(index) {
    return (event) => {
      this.updateResponseCategories(index, {
        absoluteCategory: { value: Number(event.target.value) }
      });
    };
  }

  onAddCategory() {
    const { variable, variableIndex } = this.props;
    const { responseCategories } = variable;
    const newCategory = {
      absoluteCategory: {
        value: getMinResponseCategoryValue(variable, responseCategories.length),
        description: {}
      },
      feedback: {},
      improvementSuggestions: {}
    };

    this.props.dispatchUpdateVariable(variableIndex, {
      responseCategories: [...responseCategories, newCategory]
    });
  }

  onRemoveCategory(index) {
    return () => {
      const responseCategories =
        this.props.variable.responseCategories.filter((_, idx) => idx !== index);
      this.props.dispatchUpdateVariable(this.props.variableIndex, {
        responseCategories
      });
    };
  }

  onLocalizedRTEChange(index) {
    return (lang, name) => (value) => {
      const changed = {
        [name]: { [lang]: value }
      };
      this.updateResponseCategories(index, changed);
    };
  }

  updateResponseCategories(index, changed) {
    const responseCategories = assignArrayItemInObject(
      this.props.variable.responseCategories, index, changed
    );
    this.props.dispatchUpdateVariable(this.props.variableIndex, { responseCategories });
  }

  renderValueInput(min, max, current, index) {
    return (
      <select
        className={styles.categoryValueInput}
        value={current}
        onChange={this.onCategoryValueChange(index)}
        disabled={this.props.lockedFromEditing}
      >
        {range(min, max + 1).map((value) => <option key={value} value={value}>{value}</option>)}
      </select>
    );
  }

  renderResponseCategoryOptionsRow(index) {
    const { variable, translate: t } = this.props;
    const category = variable.responseCategories[index].absoluteCategory;
    const minValue = getMinResponseCategoryValue(variable, index);
    const maxValue = getMaxResponseCategoryValue(variable, index);

    return (
      <tr key={index} className={styles.categoryRow}>
        <td className={`${styles.categoryValue} ${sharedStyles.center}`}>
          {index === 0
            ? minValue : this.renderValueInput(minValue, maxValue, category.value, index)}
        </td>
        <td className={`${styles.categoryValueSeparator} ${sharedStyles.center}`}>-</td>
        <td className={`${styles.categoryValue} ${sharedStyles.center}`}>{maxValue}</td>
        {AVAILABLE_LANGUAGES.map((lang) => (
          <td key={lang} className={styles.categoryDescription}>
            <ResponseCategoryDescription
              value={category.description[lang] || ''}
              placeholder={t(`inLanguage.${lang}`)}
              onChange={this.onCategoryDescriptionChange}
              categoryIndex={index}
              lang={lang}
            />
          </td>
        ))}
        <td className={styles.removeCategory}>{index !== 0
          && (
          <button
            type="button"
            onClick={this.onRemoveCategory(index)}
            className="actionButton icon--trash iconRemove"
            disabled={this.props.lockedFromEditing}
          />
          )}
        </td>
      </tr>
    );
  }

  renderCategoryTab(category, index) {
    const absolute = category.absoluteCategory;
    const { lang, variable } = this.props;

    return (
      <TabPane key={absolute.value} label={getTranslation(absolute.description, lang) || ''}>
        <div className={`${editorStyles.contentContainer}`}>
          <div className={`${editorStyles.contentColumn} ${editorStyles.sideBar}`}>
            <h2 className={editorStyles.columnTitle}>
              <Translate id={`${TRANSLATION_NAMESPACE}.feedback`} />
            </h2>
            <AbsoluteVariableSummary
              label=""
              stats={variableToStats(variable, category)}
              lang={lang}
            />
            <LocalizedRichTextArea
              name="feedback"
              values={category.feedback}
              onInputChange={this.onLocalizedRTEChange(index)}
              placeholderTranslationNamespace="inLanguage"
            />
          </div>
          <div className={`${editorStyles.contentColumn} ${editorStyles.mainContent}`}>
            <h2 className={editorStyles.columnTitle}>
              <Translate id={`${TRANSLATION_NAMESPACE}.improvementSuggestion`} />
            </h2>
            <LocalizedRichTextArea
              name="improvementSuggestions"
              values={category.improvementSuggestions}
              onInputChange={this.onLocalizedRTEChange(index)}
              placeholderTranslationNamespace="inLanguage"
            />
          </div>
        </div>
      </TabPane>
    );
  }

  render() {
    const { variable } = this.props;
    const lastCategoryHasMaxValue =
      last(variable.responseCategories).absoluteCategory.value === getMaxVariableValue(variable);
    const addValueButtonClass = `button--action icon--plus ${lastCategoryHasMaxValue && 'disabled'}`;

    return (
      <div>
        <h3><Translate id={`${TRANSLATION_NAMESPACE}.values`} /></h3>
        <p><Translate id={`${TRANSLATION_NAMESPACE}.valuesDescription`} /></p>

        <div className={styles.categories}>
          <table>
            <tbody>
              {variable.responseCategories.map((_, idx) =>
                this.renderResponseCategoryOptionsRow(idx))}
            </tbody>
          </table>
          <button
            type="button"
            className={addValueButtonClass}
            onClick={this.onAddCategory}
            disabled={this.props.lockedFromEditing || lastCategoryHasMaxValue}
          >
            <Translate id={`${TRANSLATION_NAMESPACE}.addValue`} />
          </button>
        </div>

        <TabsContainer>
          {variable.responseCategories
            .map((category, index) => this.renderCategoryTab(category, index))}
        </TabsContainer>
      </div>
    );
  }
}

AbsoluteVariableFeedback.propTypes = {
  variableIndex: number.isRequired,
  variable: variableEditorType.isRequired,
  dispatchUpdateVariable: func.isRequired,
  lockedFromEditing: bool.isRequired,
  ...langType
};

const mapDispatchToProps = (dispatch) => ({
  dispatchUpdateVariable: (index, variable) =>
    dispatch(updateVariableAction(index, variable))
});

export default withLang(connect(null, mapDispatchToProps)(AbsoluteVariableFeedback));
